import { useState, useEffect, useRef } from "react";
import { router } from "../data/Services/ordersRouter.jsx";

/**
 * Acha a posição do símbolo exibido, tentando na ordem: contrato traduzido pelo
 * SERVIDOR, o próprio símbolo, e os aliases contínuos da família (FUT/FUL).
 *
 * O de/para é do servidor (`translate` = `router.translateFutSymbol`, alimentado
 * pelo campo `O`). Antes havia um contrato FIXO no código como último recurso
 * (`WING26`/`WDOG26` — fevereiro/26): em julho já não casava com nada e, se
 * casasse, devolveria a posição de OUTRO vencimento para o gráfico e para o
 * Book. Enumerar vencimento no cliente é sempre dívida com data marcada.
 *
 * @param {Object} positions - mapa símbolo → posição (router.accountData.positions)
 * @param {string} symbol - símbolo exibido (WINFUT, WINFUL, WINQ26, PETR4…)
 * @param {(s: string) => string} translate - tradução do servidor
 * @returns {{ position: Object|null, foundUnder: string|null }}
 */
export const findPositionForSymbol = (positions, symbol, translate) => {
  if (!positions || !symbol) return { position: null, foundUnder: null };

  let childSymbol = translate(symbol);
  // translateFutSymbol só resolve *FUT; para *FUL, traduz o FUT equivalente.
  if (
    childSymbol === symbol &&
    (symbol.endsWith("FUL") || symbol.endsWith("FUT"))
  ) {
    const futSymbol = symbol.substring(0, 3) + "FUT";
    const translated = translate(futSymbol);
    if (translated !== futSymbol) childSymbol = translated;
  }

  const prefix = symbol.substring(0, 3);
  const candidates = [
    childSymbol,
    symbol,
    // Aliases contínuos: a ordem pode ter sido enviada por eles.
    prefix + "FUT",
    prefix + "FUL",
  ];

  const seen = new Set();
  for (const candidate of candidates) {
    if (!candidate || seen.has(candidate)) continue;
    seen.add(candidate);
    const found = positions[candidate];
    if (found && found.quantity !== 0) {
      return { position: found, foundUnder: candidate };
    }
  }

  return { position: null, foundUnder: null };
};

// Hook to get position data for a specific symbol
// NOTE: This registers a receiver per chart - consider centralizing if too heavy
export const usePositionData = (symbol) => {
  const [position, setPosition] = useState(null);
  const lastPositionRef = useRef(null);

  useEffect(() => {
    // Function to extract position for the given symbol
    const updatePosition = () => {
      try {
        if (!router.accountData || !router.accountData.positions) {
          if (lastPositionRef.current !== null) {
            lastPositionRef.current = null;
            setPosition(null);
          }
          return;
        }

        // Resolução e busca: ver findPositionForSymbol (de/para do servidor).
        const { position: symbolPosition, foundUnder } = findPositionForSymbol(
          router.accountData.positions,
          symbol,
          (s) => router.translateFutSymbol(s),
        );

        if (symbolPosition && symbolPosition.quantity !== 0) {
          // Try different price fields in order of preference
          const positionPrice =
            symbolPosition.avgPrice ||
            symbolPosition.price ||
            symbolPosition.basis ||
            0;

          const newPosition = {
            // Símbolo ONDE a posição foi encontrada. Antes reportava sempre o
            // traduzido, mesmo quando o casamento veio por um alias.
            symbol: foundUnder,
            quantity: symbolPosition.quantity || 0,
            price: positionPrice,
            basis: symbolPosition.basis || 0,
          };

          // Only update if position actually changed
          const last = lastPositionRef.current;
          if (
            !last ||
            last.quantity !== newPosition.quantity ||
            last.price !== newPosition.price ||
            last.basis !== newPosition.basis
          ) {
            lastPositionRef.current = newPosition;
            setPosition(newPosition);
          }
        } else {
          if (lastPositionRef.current !== null) {
            lastPositionRef.current = null;
            setPosition(null);
          }
        }
      } catch (error) {
        console.warn("[usePositionData] Error getting position data:", error);
        if (lastPositionRef.current !== null) {
          lastPositionRef.current = null;
          setPosition(null);
        }
      }
    };

    // Initial position update
    updatePosition();

    // Create receiver object for account data updates
    const accountDataReceiver = {
      receiveAccountData: () => {
        // console.log('🔍 [usePositionData] Receiver triggered for symbol:', symbol);
        updatePosition();
      },
    };
    router.registerReceiver(accountDataReceiver);
    // console.log('🔍 [usePositionData] Registered receiver for symbol:', symbol);

    // Cleanup: remove receiver on unmount
    return () => {
      router.unregisterReceiver(accountDataReceiver);
    };
  }, [symbol]);

  return position;
};
